import React, { ReactNode, useEffect, useMemo } from 'react';
import { Col, Row, Stack } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { FormattedMessageDiv, FormNumericInput, FormRadio, Card, FormInput, FormSelect, TrashBinIcon, PlusCircleIcon, Button, formatSSN, Navigation } from '@odmonorepo/core-components';
import { formatDateInput, makeOptions, formatToISODate } from '@odmonorepo/odfunctions';
import { Application, Person } from 'merchant-api/types';
import * as rfc6902 from 'rfc6902';
import { useMutation } from '@tanstack/react-query';
import { patchAppDataOpts } from 'merchant-api';
import { AnalyticsInstance } from 'analytics';

type AdditionalInformationProps = {
  children?: ReactNode;
  applicationData: Application | null | undefined;
  setIsAdditionalOwnersPassed: (value: boolean) => void;
  sessionId: string;
  placementVariant: string | null;
  analytics: AnalyticsInstance;
  fincenOpenedAt?: number;
};

type AdditionalOwnersForm = { hasBeneficialOwner?: string; additionalOwner?: Partial<Person>[] };

const emptyOwner: Partial<Person> = {
  type: 'OWNER',
  firstName: '',
  lastName: '',
  middleName: '',
  dateOfBirth: '',
  addresses: [
    {
      type: 'PHYSICAL_ADDRESS',
      city: '',
      state: '',
      lineOne: '',
      lineTwo: '',
      postalCode: '',
    },
  ],
  identifiers: [
    {
      type: 'SOCIAL_SECURITY_NUMBER',
      value: '',
    },
  ],
};

export const AdditionalOwners = ({ applicationData, setIsAdditionalOwnersPassed, sessionId, placementVariant, analytics, children }: AdditionalInformationProps) => {
  const { watch, control, getValues, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'additionalOwner',
  });

  const trackCompletion = () => {
    analytics.track('fincen_step_completed', { sessionId, placementVariant, substepCompleted: 'Additional Owners' });
    setIsAdditionalOwnersPassed(true);
  };

  const { mutateAsync: patch } = useMutation({
    ...patchAppDataOpts(),
    onSuccess: () => trackCompletion(),
  });

  const ownershipIndex = useMemo(() => {
    const party = applicationData?.data?.party;
    const owner = party?.personRelationships?.find((r) => r.type === 'OWNER');
    const idx = owner ? (party?.ownership ?? []).findIndex((o) => o.personId === owner.personId) : -1;
    return idx >= 0 ? idx : 0;
  }, [applicationData]);

  const otherIndividualField = watch('otherIndividual');
  const hasBeneficialOwner = otherIndividualField === 'true';

  useEffect(() => {
    if (hasBeneficialOwner && fields.length === 0) {
      append(emptyOwner);
    }
  }, [hasBeneficialOwner, fields.length, append, remove]);

  const handleAddOwner = () => {
    if (fields.length < 3) {
      append(emptyOwner);
    }
  };

  const handleRemoveOwner = (index: number) => remove(index);

  const handleConfirm = async () => {
    const values = getValues() as any;

    if (hasBeneficialOwner && values.additionalOwner?.length) {
      const existing = applicationData?.data?.party?.persons ?? [];
      const submitted = values.additionalOwner.map((o: any, i: number) => {
        const id = o.id ?? crypto.randomUUID();
        setValue(`additionalOwner.${i}.id`, id);
        return { ...o, id, dateOfBirth: o.dateOfBirth ? formatToISODate(o.dateOfBirth) : o.dateOfBirth };
      });

      const submittedIds = new Set(submitted.map((o: any) => o.id));
      const output = {
        ...applicationData?.data,
        party: {
          ...applicationData?.data?.party,
          persons: [...existing.filter((p: any) => !submittedIds.has(p.id)), ...submitted],
        },
      };
      const patches = rfc6902.createPatch(applicationData?.data, output);
      await patch({ id: applicationData!.id, patches });
    } else {
      trackCompletion();
    }
  };

  return (
    <Stack gap={5} style={{ maxWidth: '600px' }} className="mx-auto">
      <Card bodyClassName={'p-5'}>
        <Stack gap={5}>
          <FormattedMessageDiv className="h4 mb-0" id={'additionalInformation.additionalOwners.title'} />
          <FormNumericInput
            name={`party.ownership.${ownershipIndex}.ownershipPct`}
            label={<FormattedMessageDiv id="formInput.party.ownership.ownershipPct" />}
            data-testid="ownershipPct"
            suffix="%"
            min={1}
            max={100}
            readOnly={true}
            className="label-expanded"
          />
          <Row xs={1} md={1} className="g-3">
            <Col>
              <FormRadio
                name="otherIndividual"
                nameValues={{
                  percentage: (
                    <strong>
                      <FormattedMessage id={'additionalOwners.percentage'} />
                    </strong>
                  ),
                }}
                data-testid="otherIndividual"
                options={['true', 'false']}
                inline={false}
              />
            </Col>
          </Row>
          {hasBeneficialOwner && (
            <Stack gap={5}>
              {fields.map((field, index) => (
                <Card key={field.id} className="bg-light-gray border-0 shadow-none" bodyClassName="p-4">
                  <Stack gap={5}>
                    <Stack gap={2} direction="horizontal" className="justify-content-between">
                      <FormattedMessageDiv className="fw-bold" id="additionalOwners.ownerHeading" values={{ number: index + 1 }} />
                      {index > 0 && (
                        <Button style={{ width: '20px' }} variant="link" className="p-0 border-0" onClick={() => handleRemoveOwner(index)}>
                          <TrashBinIcon className="justify-content-end me-4" />
                        </Button>
                      )}
                    </Stack>
                    <FormInput<AdditionalOwnersForm> name={`additionalOwner.${index}.firstName`} data-testid={`owner-${index}-firstName`} className="bg-light" useYupErrors />
                    <FormInput<AdditionalOwnersForm> name={`additionalOwner.${index}.lastName`} data-testid={`owner-${index}-lastName`} className="bg-light" useYupErrors />
                    <FormInput<AdditionalOwnersForm> name={`additionalOwner.${index}.addresses.0.city`} data-testid={`owner-${index}-addressCity`} className="bg-light" useYupErrors />
                    <FormSelect<AdditionalOwnersForm>
                      name={`additionalOwner.${index}.addresses.0.state`}
                      data-testid={`owner-${index}-addressState`}
                      className="bg-light"
                      options={makeOptions('business.address.state', 51)}
                      blankLabel=""
                      useYupErrors
                    />
                    <FormInput<AdditionalOwnersForm>
                      className="bg-light"
                      name={`additionalOwner.${index}.dateOfBirth`}
                      data-testid={`owner-${index}-dateOfBirth`}
                      formatValue={formatDateInput}
                      controlProps={{ inputMode: 'numeric' }}
                      useYupErrors
                    />
                    <FormInput<AdditionalOwnersForm>
                      className="fs-mask bg-light"
                      name={`additionalOwner.${index}.identifiers.0.value`}
                      data-testid={`owner-${index}-ssn`}
                      stripFormatOnSave
                      useYupErrors
                      formatValue={formatSSN}
                      label={<FormattedMessageDiv id="formInput.ssn" />}
                      controlProps={{
                        inputMode: 'numeric',
                        onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                          setTimeout(() => e.target.select(), 0);
                        },
                        onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                          (e.target as HTMLInputElement).select();
                        },
                      }}
                    />

                    {fields.length < 3 && index === fields.length - 1 && (
                      <Button variant="link" className="btn btn-link p-0 align-self-start" onClick={handleAddOwner}>
                        <Stack gap={2} direction="horizontal">
                          <PlusCircleIcon />
                          <FormattedMessageDiv id="additionalOwner.addAnotherOwner" className="d-inline-block mt-1" />
                        </Stack>
                      </Button>
                    )}
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
        </Stack>
        {children}
        <Navigation
          handleNext={handleConfirm}
          stacked={true}
          continueOnly={true}
        />
      </Card>
    </Stack>
  );
};
